import Axios from 'axios';
import { storage } from '@/utils/storage';
import { API_URL } from '@/config';
import { reportApiFailure, reportNetworkFailure } from '@/lib/sentry-report';

export const BASE_URL = API_URL;

const defaultOptions = {
  baseURL: BASE_URL,
  timeout: 300000,
};

const handleApiError = (err: any) => {
  let message: string = err.response?.data?.error || err.message;
  if (Object.keys(err.response?.data).length > 0) {
    const key = Object.keys(err.response?.data)[0];
    message = `${key}: ${err.response?.data[key][0]}`;
  }

  return message;
};

export async function getApiAuth() {
  const instance = Axios.create({
    ...defaultOptions,
    baseURL: BASE_URL,
  });

  instance.interceptors.response.use(
    (response) => response,
    (err) => {
      const method = (err.config?.method || 'GET').toUpperCase();
      const endpoint = err.config?.url || 'desconhecido';
      if (err.response) {
        reportApiFailure(method, endpoint, err.response.status, handleApiError(err));
      } else {
        reportNetworkFailure(method, endpoint, err);
      }

      return handleApiError(err);
    },
  );

  return instance;
}

const refreshToken = async () => {
  const api = await getApiAuth();
  return api.post('/refresh-token/');
};

export async function getConfig(otherParams = {}) {
  const token = storage.getToken();

  return {
    ...defaultOptions,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    ...otherParams,
  };
}

export default async function getApi(otherParams = {}) {
  const config = await getConfig(otherParams);

  const instance = Axios.create({
    ...config,
  });

  instance.interceptors.response.use(
    (response) => response,
    (err) => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async (resolve, reject) => {
        const originalReq = err.config;

        // eslint-disable-next-line no-underscore-dangle
        const isRetryRequest = err.config && !err.config.__isRetryRequest;
        if (err.response && err.response.status === 401 && isRetryRequest) {
          // eslint-disable-next-line no-underscore-dangle
          originalReq._retry = true;

          const res = refreshToken()
            .then(async (accessTokenRetry) => {
              storage.setToken(String(accessTokenRetry));
              originalReq.headers.Authorization = `Bearer ${accessTokenRetry}`;

              return Axios(originalReq);
            })
            .catch(() => {
              storage.clearAll();
              window.location.href = '/login';
            });

          resolve(res);
        }

        // Reporta ao Sentry apenas falhas inesperadas: 5xx (erro real do
        // servidor) ou falha de rede. 401/403/404/validação (4xx) é fluxo
        // esperado da aplicação e não deve virar evento — só geraria ruído.
        const method = (err.config?.method || 'GET').toUpperCase();
        const endpoint = err.config?.url || 'desconhecido';
        if (err.response) {
          reportApiFailure(method, endpoint, err.response.status, handleApiError(err));
        } else {
          reportNetworkFailure(method, endpoint, err);
        }

        reject({
          message: handleApiError(err)
        });
      });
    },
  );

  return instance;
}
